// src/components/SimpleVWAPChart.tsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { fetchSimpleVWAP } from '../api/series';
import { useMarket } from '../context/MarketContext';

interface VWAPDataPoint {
  time: string;
  timeValue: number; // Numeric timestamp for X-axis (milliseconds)
  vwap: number;
  timestamp: string;
}

export interface SimpleVWAPChartProps {
  symbol?: string;
  height?: number;
}

export default function SimpleVWAPChart({ 
  symbol = 'LPPJ',
  height = 300
}: SimpleVWAPChartProps) {
  const {
    selectedSymbol,
    vwapDataBySymbol,
    dataVersions,
    labelForSecId
  } = useMarket();
  
  const actualSymbol = symbol || selectedSymbol;
  const [data, setData] = useState<VWAPDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef<VWAPDataPoint[]>([]);
  const lastProcessedVwapVersionRef = useRef<number>(0);
  const latestTimestampRef = useRef<string | null>(null);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // console.log('Loading initial VWAP data for symbol:', actualSymbol);
      
      // First, try to use WebSocket data
      const wsVwapData = vwapDataBySymbol.get(actualSymbol.toUpperCase()) || [];
      
      let chartData: VWAPDataPoint[] = [];
      
      // If we have WebSocket data, use it
      if (wsVwapData.length > 0) {
        // console.log('Using WebSocket VWAP data:', wsVwapData.length, 'points');
        
        chartData = wsVwapData.map((point: any) => {
          const timestamp = new Date(point.timestamp).getTime();
          return {
            time: new Date(point.timestamp).toLocaleTimeString(),
            timeValue: timestamp,
            vwap: point.vwap,
            timestamp: point.timestamp
          };
        });
        
        // Sort chart data by timestamp in ascending order
        chartData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        dataRef.current = chartData;
        setData(chartData);
        
        if (chartData.length > 0) {
          latestTimestampRef.current = chartData[chartData.length - 1].timestamp;
        }
        
        // console.log('Initial chart data prepared from WebSocket:', chartData.length, 'points');
        setLoading(false);
        return;
      }
      
      // Fallback to API if no WebSocket data available (disabled - VWAP comes from WebSocket only)
      // VWAP data should come from WebSocket, HTTP API is not available
      // console.log('No WebSocket data available yet, waiting for WebSocket data...');
      setLoading(false);
      return;
    } catch (err) {
      // console.error('Error loading initial VWAP data:', err);
      if (vwapDataBySymbol.get(actualSymbol.toUpperCase())?.length === 0) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [actualSymbol, vwapDataBySymbol]);

  const loadNewData = useCallback(async () => {
    try {
      // Check for new WebSocket data
      const wsVwapData = vwapDataBySymbol.get(actualSymbol.toUpperCase()) || [];
      const latestTimestamp = latestTimestampRef.current;
      const currentData = dataRef.current;
      
      // Only process if we have new data
      if (wsVwapData.length <= currentData.length) {
        return; // No new data
      }
      
      // Process new WebSocket VWAP data (only the new points)
      const newPoints = wsVwapData.slice(currentData.length);
      const newVwapData = newPoints
        .filter((point: any) => !latestTimestamp || point.timestamp > latestTimestamp)
        .map((point: any) => {
          const timestamp = new Date(point.timestamp).getTime();
          return {
            time: new Date(point.timestamp).toLocaleTimeString(),
            timeValue: timestamp,
            vwap: point.vwap,
            timestamp: point.timestamp
          };
        });
      
      if (newVwapData.length > 0) {
        // console.log('Adding', newVwapData.length, 'new VWAP points from WebSocket');
        
        const combined = [...currentData, ...newVwapData];
        combined.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const trimmedData = combined.slice(-100); // Keep last 100 points
        dataRef.current = trimmedData;
        
        latestTimestampRef.current = trimmedData[trimmedData.length - 1].timestamp;
        
        // Only update state if data actually changed
        setData(prevData => {
          if (prevData.length !== trimmedData.length ||
              (prevData.length > 0 && trimmedData.length > 0 &&
               prevData[prevData.length - 1].timestamp !== trimmedData[trimmedData.length - 1].timestamp)) {
            return trimmedData;
          }
          return prevData;
        });
      }
    } catch (err) {
      // console.error('Error loading new VWAP data:', err);
    }
  }, [actualSymbol, vwapDataBySymbol]);

  useEffect(() => {
    // Reset refs when symbol changes
    lastProcessedVwapVersionRef.current = 0;
    latestTimestampRef.current = null;
    dataRef.current = [];
    
    // Load initial data
    loadInitialData();
  }, [actualSymbol, loadInitialData]);
  
  // Reactive update: Watch for version changes (when new WebSocket data arrives)
  const symbolKey = actualSymbol.toUpperCase();
  const symbolVersions = dataVersions[symbolKey];
  
  useEffect(() => {
    if (!symbolVersions) return;
    
    const vwapVersion = symbolVersions.vwap || 0;
    
    // Only process if version actually increased (new data arrived)
    if (vwapVersion > lastProcessedVwapVersionRef.current) {
      lastProcessedVwapVersionRef.current = vwapVersion;
      loadNewData();
    }
  }, [symbolVersions?.vwap, actualSymbol, loadNewData]);
  
  // Reset version refs when symbol changes
  useEffect(() => {
    lastProcessedVwapVersionRef.current = 0;
  }, [actualSymbol]);
  
  // Calculate X-axis domain based on time range
  const xAxisDomain = useMemo(() => {
    if (data.length === 0) return undefined;
    const times = data.map(d => d.timeValue);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const padding = (maxTime - minTime) * 0.02;
    return [minTime - padding, maxTime + padding];
  }, [data]);
  
  // Format time for X-axis ticks
  const formatTimeTick = (tickItem: number) => {
    const date = new Date(tickItem);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#94a3b8' }}>Loading VWAP data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#ef4444' }}>Error: {error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#94a3b8' }}>No VWAP data available for {symbol}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      borderRadius: 8, 
      padding: 16,
      border: '1px solid #334155'
    }}>
      <h3 style={{ 
        margin: '0 0 16px 0', 
        color: '#f1f5f9', 
        fontSize: 16,
        fontWeight: 600
      }}>
        VWAP Chart - {labelForSecId(actualSymbol)} ({actualSymbol})
      </h3>
      <div style={{ height: typeof height === 'number' ? `${height}px` : height, width: '100%', minHeight: typeof height === 'number' ? `${height}px` : height, boxSizing: 'border-box' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="vwapGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8}/> {/* Blue */}
                <stop offset="50%" stopColor="#8b5cf6" stopOpacity={0.6}/> {/* Purple */}
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0}/> {/* Fade to transparent */}
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              type="number"
              dataKey="timeValue"
              domain={xAxisDomain}
              stroke="#94a3b8"
              fontSize={12}
              tickFormatter={formatTimeTick}
              allowDecimals={false}
            />
            <YAxis 
              stroke="#94a3b8"
              fontSize={12}
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value: number) => `${value.toFixed(2)}`}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 6,
                color: '#f1f5f9'
              }}
              labelFormatter={(label: number) => {
                return new Date(label).toLocaleTimeString();
              }}
              formatter={(value: number) => [`${value.toFixed(2)} TZS`, 'VWAP']}
            />
            <Area 
              type="monotone" 
              dataKey="vwap" 
              stroke="#6366f1" 
              strokeWidth={2}
              fill="url(#vwapGradient)"
              fillOpacity={1}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{ 
        marginTop: 8, 
        fontSize: 12, 
        color: '#64748b',
        textAlign: 'center'
      }}>
        {data.length} data points | Last update: {data[data.length - 1]?.time}
      </div>
    </div>
  );
}
